import React from "react";
import { MoveLeft, MoveRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { formatNumber } from "src/utils/numberFormat";

type CustomPaginationProps = {
  current: number;
  pageSize: number;
  total: number;
  onChange: (_page: number) => void;
  showTotal?: boolean;
  className?: string;
};

const CustomPagination: React.FC<CustomPaginationProps> = ({
  current,
  pageSize,
  total,
  onChange,
  showTotal = true,
  className,
}) => {
  const { t, i18n } = useTranslation();
  const totalPages = Math.ceil(total / pageSize);
  const start = total === 0 ? 0 : (current - 1) * pageSize + 1;
  const end = total === 0 ? 0 : Math.min(current * pageSize, total);
  const localizedStart = formatNumber(start, {}, i18n.language);
  const localizedEnd = formatNumber(end, {}, i18n.language);
  const localizedTotal = formatNumber(total, {}, i18n.language);
  const localizedCurrent = formatNumber(current, {}, i18n.language);

  const isArabic = (i18n.resolvedLanguage ?? i18n.language ?? "en")
    .toLowerCase()
    .startsWith("ar");
  const PreviousIcon = isArabic ? MoveRight : MoveLeft;
  const NextIcon = isArabic ? MoveLeft : MoveRight;

  return (
    <div
      className={`custom-pagination custom-pagination--compact ${
        showTotal ? "" : "custom-pagination--no-total"
      } ${className || ""}`}
    >
      {showTotal && (
        <span className="pagination-total">
          {localizedStart}-{localizedEnd} {t("pagination.of")} {localizedTotal}
        </span>
      )}

      <div className="pagination-controls">
        <button
          className="pagination-btn"
          type="button"
          aria-label={t("pagination.previousPage")}
          disabled={current === 1}
          onClick={() => current > 1 && onChange(current - 1)}
        >
          <PreviousIcon size={18} />
        </button>

        <span className="pagination-page">{localizedCurrent}</span>

        <button
          className="pagination-btn"
          type="button"
          aria-label={t("pagination.nextPage")}
          disabled={current === totalPages}
          onClick={() => current < totalPages && onChange(current + 1)}
        >
          <NextIcon size={18} />
        </button>
      </div>
    </div>
  );
};

export default CustomPagination;
